import { useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const emptyForm = { name: "", email: "", phone: "", message: "" };

export const ChatWidget = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (pathname.startsWith("/admin")) return null;

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async () => {
    if (!form.name.trim() || !/\S+@\S+\.\S+/.test(form.email) || !form.message.trim()) {
      toast.error("Please fill in your name, a valid email, and your question");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/chat-inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, page: pathname }),
      });
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.detail || "Could not send your message. Please try again.");
        return;
      }
      setSent(true);
      setForm(emptyForm);
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.2 }}
            data-testid="chat-widget-panel"
            className="fixed bottom-40 md:bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm rounded-2xl shadow-2xl border border-border/60 bg-white overflow-hidden"
          >
            <div className="bg-[#1A3C34] px-5 py-4">
              <p className="font-heading text-white text-lg">Questions? We're here!</p>
              <p className="font-body text-white/60 text-xs mt-0.5">
                Send us a message and we'll reply to your email — usually within a few hours.
              </p>
            </div>
            <div className="p-5">
              {sent ? (
                <div data-testid="chat-widget-success" className="text-center py-6">
                  <CheckCircle2 className="mx-auto text-primary mb-3" size={36} />
                  <p className="font-heading text-lg text-foreground mb-1">Message sent!</p>
                  <p className="font-body text-sm text-muted-foreground mb-4">
                    Our team has been notified and will get back to you shortly.
                  </p>
                  <Button data-testid="chat-widget-new-message" variant="outline" size="sm" onClick={() => setSent(false)} className="font-body rounded-full">
                    Send another message
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="chat-widget-name" placeholder="Your name *" value={form.name} onChange={update("name")} className="font-body text-sm" />
                    <Input data-testid="chat-widget-phone" placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} className="font-body text-sm" />
                  </div>
                  <Input data-testid="chat-widget-email" type="email" placeholder="Email *" value={form.email} onChange={update("email")} className="font-body text-sm" />
                  <Textarea
                    data-testid="chat-widget-message"
                    placeholder="How can we help? Availability, pets, directions, group events..."
                    rows={3}
                    value={form.message}
                    onChange={update("message")}
                    className="font-body text-sm"
                  />
                  <Button data-testid="chat-widget-send" onClick={submit} disabled={sending} className="w-full bg-accent hover:bg-accent/90 text-white font-body rounded-full">
                    {sending ? <Loader2 size={15} className="animate-spin" /> : <><Send size={14} className="mr-2" /> Send Message</>}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="chat-widget-bubble"
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close chat" : "Chat with us"}
        className="fixed bottom-[5.5rem] md:bottom-6 right-5 z-50 h-14 w-14 rounded-full bg-[#1A3C34] text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </button>
    </>
  );
};
